import { Card, CardContent } from "@/components/ui/card";
import { formatRewardAmount } from "@/lib/textUtils";
import { type LeaderboardUser } from "@/lib/schema";

interface LeaderboardSectionProps {
  loading?: boolean;
  leaderboardData?: LeaderboardUser[];
}

const LeaderboardItemSkeleton = () => (
  <div className="grid grid-cols-3 gap-4 items-center py-3 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-8"></div>
    <div className="h-4 bg-gray-200 rounded w-24"></div>
    <div className="h-4 bg-gray-200 rounded w-20"></div>
  </div>
);

const LeaderboardSkeleton = () => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-6">
      <div className="mb-6">
        <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
      </div>
      <div className="space-y-4">
        {[...Array(10)].map((_, i) => (
          <LeaderboardItemSkeleton key={i} />
        ))}
      </div>
    </CardContent>
  </Card>
);

const EmptyLeaderboardState = () => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-6">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 space-grotesk">Top 10 Leaderboard</h3>
      </div>
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">No Rewards Earned Yet</h4>
        <p className="text-gray-500 text-sm">Users will appear here once they earn their first rewards from successful referrals.</p>
      </div>
    </CardContent>
  </Card>
);

const LeaderboardItem = ({ user, rank }: { user: LeaderboardUser; rank: number }) => {
  return (
    <div 
      className="grid grid-cols-3 gap-4 items-center py-3" 
      data-testid={`leaderboard-item-${rank - 1}`}
    >
      <div className="text-gray-900 font-bold space-grotesk">{rank}</div>
      <div className="flex items-center gap-3">
        <span className="font-medium text-gray-900 space-grotesk">{user.name}</span>
      </div>
      <div className="text-gray-900 font-bold space-grotesk">R{formatRewardAmount(user.totalEarnings)}</div>
    </div>
  );
};

export const LeaderboardSection = ({ loading, leaderboardData }: LeaderboardSectionProps) => {
  const isLoading = loading;

  if (isLoading) {
    return <LeaderboardSkeleton />;
  }
  
  if (!leaderboardData || leaderboardData.length === 0) {
    return <EmptyLeaderboardState />;
  }

  return (
    <Card className="border-0 shadow-sm" data-testid="leaderboard-card">
      <CardContent className="p-6">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 space-grotesk">Top 10 Leaderboard</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-sm font-medium text-gray-500 space-grotesk pb-2 border-b">
            <div>RANK</div>
            <div>NAME</div>
            <div>REWARDS EARNED</div>
          </div>
          {leaderboardData.slice(0, 10).map((user, index) => (
            <LeaderboardItem key={user.id} user={user} rank={index + 1} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};